import { useCallback, useMemo } from 'react'
import { DEFAULT_PARAMS, keepOnlyParams, Params, useParamsHandler } from './useParamsHandler'
import { keepOnlyAttributes, useAttributesQuery } from './useAttributesQuery'

type AllowedParamsUpdateObject = { [k in keyof Params]: boolean }

interface UseCompetitionProps<T> {
    initDataFromPhpFirstRendering: T & Params
    allowedParamsUpdatesProps?: Partial<AllowedParamsUpdateObject>
    onFinish?: (v: { message: string }) => void
}

interface UseCompetitionReturn<T> {
    attributes: T
    params: Params
    isLoading: boolean
    // todo: improve api: ({key, value}: {key: keyof Params, value: string})
    setParams: (key: keyof Params, value: string) => void
}

// The implementation should ensure that onFinish is always called after setParams is called.
export const useCompetitionProps = <T>({
    initDataFromPhpFirstRendering,
    allowedParamsUpdatesProps = {},
    onFinish,
}: UseCompetitionProps<T>): UseCompetitionReturn<T> => {
    const [params, _setParams] = useParamsHandler<Params>({
        initParams: {
            ...DEFAULT_PARAMS,
            ...keepOnlyParams(initDataFromPhpFirstRendering),
        },
        onFinish,
    })

    const { attributes, isLoading } = useAttributesQuery<T>({
        initAttributes: keepOnlyAttributes(initDataFromPhpFirstRendering),
        initParams: {
            ...DEFAULT_PARAMS,
            ...keepOnlyParams(initDataFromPhpFirstRendering),
        },
        currentParams: params,
        onFinish,
    })

    const allowedParamsUpdates: AllowedParamsUpdateObject = useMemo(
        () => ({
            ext_saison_id: allowedParamsUpdatesProps.ext_saison_id ?? false,
            url_competition_type: allowedParamsUpdatesProps.url_competition_type ?? false,
            url_structure: allowedParamsUpdatesProps.url_structure ?? false,
            url_competition: allowedParamsUpdatesProps.url_competition ?? false,
            ext_poule_id: allowedParamsUpdatesProps.ext_poule_id ?? false,
            numero_journee: allowedParamsUpdatesProps.numero_journee ?? false,
            ext_equipe_id: allowedParamsUpdatesProps.ext_poule_id ?? false,
            ext_rencontre_id: allowedParamsUpdatesProps.ext_rencontre_id ?? false,
        }),
        [
            allowedParamsUpdatesProps.ext_poule_id,
            allowedParamsUpdatesProps.ext_saison_id,
            allowedParamsUpdatesProps.numero_journee,
            allowedParamsUpdatesProps.url_competition,
            allowedParamsUpdatesProps.url_competition_type,
            allowedParamsUpdatesProps.url_structure,
            allowedParamsUpdatesProps.ext_rencontre_id,
        ],
    )

    const setParams = useCallback(
        (params_key: string, value: number | string) => {
            if (!allowedParamsUpdates[params_key]) {
                onFinish?.({ message: 'UPDATE_NOT_ALLOWED' })
                return
            }
            switch (params_key) {
                case 'ext_saison_id':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: String(value),
                    })
                    break
                case 'url_competition_type':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: String(value),
                    })
                    break
                case 'url_structure':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: params.url_competition_type,
                        url_structure: String(value),
                    })
                    break
                case 'url_competition':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: params.url_competition_type,
                        url_structure: params.url_structure,
                        url_competition: String(value),
                    })
                    break
                case 'ext_poule_id':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: params.url_competition_type,
                        url_structure: params.url_structure,
                        url_competition: params.url_competition,
                        ext_poule_id: String(value),
                    })
                    break
                case 'numero_journee':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: params.url_competition_type,
                        url_structure: params.url_structure,
                        url_competition: params.url_competition,
                        ext_poule_id: params.ext_poule_id,
                        numero_journee: String(value),
                    })
                    break
                case 'ext_equipe_id':
                    _setParams({
                        ...DEFAULT_PARAMS,
                        ext_saison_id: params.ext_saison_id,
                        url_competition_type: params.url_competition_type,
                        url_structure: params.url_structure,
                        url_competition: params.url_competition,
                        ext_equipe_id: String(value),
                    })
                    break
                default:
                    onFinish?.({ message: 'UNHANDLED_PARAMS' })
            }
        },
        [
            allowedParamsUpdates,
            onFinish,
            _setParams,
            params.ext_saison_id,
            params.url_competition_type,
            params.url_structure,
            params.url_competition,
            params.ext_poule_id,
        ],
    )

    return {
        params,
        attributes,
        isLoading,
        setParams,
    }
}
